'use client';

import { useEffect, useRef } from 'react';
import type {
  ElementRef,
  SourceHTMLAttributes,
  VideoHTMLAttributes,
} from 'react';
import { cn } from '@noirproperties/ui/lib/utils';
import { useVideoState } from './video-hooks';

export type Source = SourceHTMLAttributes<HTMLSourceElement>['src'];
export type VideoPoster = VideoHTMLAttributes<HTMLVideoElement>['poster'];

export type VideoSources = {
  mp4: Source;
  webm?: Source;
  h265?: Source;
};

type VideoPlayerProps = {
  autoPlay?: boolean;
  sources: VideoSources;
  poster?: VideoPoster;
  className?: string;
};

export function VideoPlayer({
  sources,
  poster,
  className,
  autoPlay = true,
}: VideoPlayerProps) {
  const videoRef = useRef<ElementRef<'video'>>(null);
  const { status } = useVideoState();

  useEffect(() => {
    const video = videoRef.current;

    if (!video) return;

    const playVideo = () => {
      video.play().catch(() => {
        // noop
      });
      video.muted = false;
    };

    const muteVideo = () => {
      video.muted = true;
    };

    if (status === 'PLAYING') {
      playVideo();
    } else {
      muteVideo();
    }
  }, [status]);

  return (
    <video
      autoPlay={autoPlay}
      className={cn(
        'absolute inset-0 -z-10 h-full w-full overflow-hidden bg-gunmetal object-cover object-center',
        className,
      )}
      loop
      muted
      playsInline
      poster={poster}
      preload="auto"
      ref={videoRef}
    >
      {sources.h265 ? (
        <source src={sources.h265} type='video/mp4; codecs="hvc1"' />
      ) : null}
      {sources.webm ? (
        <source src={sources.webm} type="video/webm; codecs=vp9" />
      ) : null}
      <source src={sources.mp4} type="video/mp4" />
    </video>
  );
}
